import React, { useState, useEffect } from "react";
import { PlusSmallIcon } from "@heroicons/react/24/outline";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import BaseModal from "../../popups/model";
import { Input } from "../../form";
import {
  createRegionalEvent,
  getRegionalEvents,
  getProfileTypes,
  updateRegionalEventOrder
} from "../../../service";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import AdminPanelButton from "../../form/button/adminPanelButton";
import OrgRegionalEvent from "./orgRegionalEvent";

export default function OrgRegionalEvents({ prefilledData }) {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [profileTypes, setProfileTypes] = useState([]);
  const [selectedProfileType, setSelectedProfileType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updatedOrderKey, setUpdatedOrderKey] = useState([]);

  const submitHandler = async () => {
    if (!selectedProfileType) {
      toast.error("Please select a profile type first");
      return;
    }

    setLoading(true);

    try {
      const postBody = {
        ...formik.values,
        profileTypeId: selectedProfileType._id,
        orderKey: events.length + 1
      };

      const res = await createRegionalEvent({ ...postBody }, prefilledData._id);
      getAdminRegionalEvents();

      toast.success(res.data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
      formik.resetForm();
      setOpen(!open);
    }
  };

  async function getAdminRegionalEvents() {
    try {
      if (!prefilledData || !selectedProfileType) return;
      const events = await getRegionalEvents(prefilledData._id);
      const filteredEvents = events.data.filter(
        (event) => event.profileTypeId?._id === selectedProfileType._id
      );
      filteredEvents.sort(function (a, b) {
        return a.orderKey - b.orderKey;
      });
      setEvents(filteredEvents);
    } catch (err) {
      toast.error("error while fetching regional events");
      console.log(err);
    }
  }

  async function getAdminProfileTypes() {
    try {
      if (!prefilledData) return;
      const profileTypes = await getProfileTypes(prefilledData._id);
      profileTypes.data.sort(function (a, b) {
        return a.orderKey - b.orderKey;
      });
      setProfileTypes(profileTypes.data);
    } catch (err) {
      toast.error("error while fetching profile types");
      console.log(err);
    }
  }

  useEffect(() => {
    getAdminProfileTypes();
  }, [prefilledData]);

  useEffect(() => {
    if (selectedProfileType) {
      getAdminRegionalEvents();
    } else {
      setEvents([]);
    }
  }, [selectedProfileType, prefilledData]);

  useEffect(() => {
    if (updatedOrderKey.length > 0) {
      updateRegionalEventOrder(prefilledData._id, { events: updatedOrderKey });
    }
  }, [updatedOrderKey, prefilledData._id]);

  const formik = useFormik({
    initialValues: {
      name_de: "",
      name_en: "",
      name_fr: "",
      name_it: ""
    },
    onSubmit: submitHandler
  });

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const tempOrderKey = events[result.source.index].orderKey;
    events[result.source.index].orderKey = events[result.destination.index].orderKey;
    events[result.destination.index].orderKey = tempOrderKey;

    const temp = events[result.source.index];
    events[result.source.index] = events[result.destination.index];

    events[result.destination.index] = temp;
    const newData = updatedOrderKey.filter((item) => {
      if (
        item._id == events[result.source.index]._id ||
        item._id == events[result.destination.index]._id
      ) {
        return false;
      } else {
        return true;
      }
    });

    setUpdatedOrderKey([...newData, events[result.source.index], events[result.destination.index]]);
    setEvents([...events]);
  };

  return (
    <>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="mt-8 flow-root">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Profile Type
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={selectedProfileType?._id || ""}
              onChange={(e) => {
                const profileType = profileTypes.find((pt) => pt._id === e.target.value);
                setSelectedProfileType(profileType);
              }}>
              <option value="">Select a profile type</option>
              {profileTypes.map((profileType) => (
                <option key={profileType._id} value={profileType._id}>
                  {profileType?.name}
                </option>
              ))}
            </select>
          </div>

          {selectedProfileType && (
            <>
              <div className="overflow-x-auto">
                <div className="inline-block min-w-full py-2 align-middle">
                  <table className="min-w-full divide-y divide-gray-300">
                    <DragDropContext onDragEnd={onDragEnd}>
                      <Droppable droppableId="characters">
                        {(provided) => (
                          <tbody
                            className="divide-y divide-gray-200"
                            {...provided.droppableProps}
                            ref={provided.innerRef}>
                            {events.map((event, index) => {
                              return (
                                <Draggable key={event._id} draggableId={event._id} index={index}>
                                  {(provided) => {
                                    return (
                                      <tr
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}>
                                        <td
                                          colSpan="6"
                                          className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0">
                                          <OrgRegionalEvent
                                            event={event}
                                            prefilledData={prefilledData}
                                            getAdminRegionalEvents={getAdminRegionalEvents}
                                            updatedOrderKey={updatedOrderKey}
                                          />
                                        </td>
                                      </tr>
                                    );
                                  }}
                                </Draggable>
                              );
                            })}
                            <div
                              className="cursor-pointer"
                              onClick={() => {
                                setOpen(!open);
                              }}>
                              <PlusSmallIcon
                                className={`w-7 h-7 stroke-2 stroke-white bg-[#E3E3E3] rounded-full`}
                              />
                            </div>
                            {provided.placeholder}
                          </tbody>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <BaseModal open={open} setOpen={setOpen} title={"Create Regional Event"}>
        <form onSubmit={formik.handleSubmit}>
          <div className="grid grid-cols-2 gap-2">
            <Input
              label="DE"
              name="name_de"
              formik={formik}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik?.values["name_de"]}
              error={formik?.errors["name_de"]}
              touched={formik?.touched["name_de"]}
            />
            <Input
              label="EN"
              name="name_en"
              formik={formik}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik?.values["name_en"]}
              error={formik?.errors["name_en"]}
              touched={formik?.touched["name_en"]}
            />
            <Input
              label="FR"
              name="name_fr"
              formik={formik}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik?.values["name_fr"]}
              error={formik?.errors["name_fr"]}
              touched={formik?.touched["name_fr"]}
            />
            <Input
              label="IT"
              name="name_it"
              formik={formik}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik?.values["name_it"]}
              error={formik?.errors["name_it"]}
              touched={formik?.touched["name_it"]}
            />
          </div>

          <div className="mt-5 sm:mt-4 grid grid-cols-2 gap-4">
            <AdminPanelButton
              type="submit"
              isSave={true}
              loading={loading}
              bgColor="#FC9E5A"
              textColor="#FFFFFF"
              color="white"
              onClick={() => {}}>
              create
            </AdminPanelButton>
          </div>
        </form>
      </BaseModal>
    </>
  );
}
